import { makeAutoObservable, observable, runInAction } from 'mobx';
import { PromoCode } from '../model/professional/promo-code-model';

export class PromoCodeStore {
  promoCodes: PromoCode[] = [];
  @observable isLoading: boolean = false;
  @observable selectedPromoCode: PromoCode | null = null;
  @observable canCreate: boolean = false;
  @observable canCreateMessage: string | null = null;

  private static instance: PromoCodeStore;

  private constructor() {
    makeAutoObservable(this);
  }

  public static getInstance(): PromoCodeStore {
    if (!PromoCodeStore.instance) {
      PromoCodeStore.instance = new PromoCodeStore();
    }
    return PromoCodeStore.instance;
  }

  /**
   * Active l'état de chargement
   */
  setIsLoading(isLoading: boolean) {
    runInAction(() => {
      this.isLoading = isLoading;
    });
  }

  /**
   * Définit la liste des codes promo
   */
  setPromoCodes(promoCodes: PromoCode[]) {
    runInAction(() => {
      this.promoCodes = promoCodes;
      this.isLoading = false;
    });
  }

  /**
   * Définit si l'utilisateur peut créer un code promo
   */
  setCanCreate(canCreate: boolean, message?: string) {
    runInAction(() => {
      this.canCreate = canCreate;
      this.canCreateMessage = message || null;
    });
  }

  /**
   * Ajoute un code promo à la liste
   */
  addPromoCode(promoCode: PromoCode) {
    runInAction(() => {
      this.promoCodes.push(promoCode);
    });
  }

  /**
   * Met à jour un code promo dans la liste
   */
  updatePromoCode(id: string, updatedData: Partial<PromoCode>) {
    runInAction(() => {
      const index = this.promoCodes.findIndex(pc => pc.id === id);
      if (index !== -1) {
        this.promoCodes[index] = { ...this.promoCodes[index], ...updatedData };
      }
    });
  }

  /**
   * Supprime un code promo de la liste
   */
  removePromoCode(id: string) {
    runInAction(() => {
      this.promoCodes = this.promoCodes.filter(pc => pc.id !== id);
    });
  }

  /**
   * Définit le code promo sélectionné par son ID
   */
  setPromoCodeById(id: string | null) {
    if (!id || !this.promoCodes) {
      console.warn('ID invalide ou promoCodes non initialisé', { id, hasPromoCodes: !!this.promoCodes });
      return;
    }

    runInAction(() => {
      const found = this.promoCodes.find(pc => pc.id === id);
      this.selectedPromoCode = found || null;
    });
  }

  /**
   * Réinitialise le store
   */
  reset() {
    runInAction(() => {
      this.promoCodes = [];
      this.selectedPromoCode = null;
      this.isLoading = false;
      this.canCreate = false;
      this.canCreateMessage = null;
    });
  }

  /**
   * Getters
   */
  get getPromoCodes() {
    return this.promoCodes;
  }

  get getActivePromoCodes() {
    return this.promoCodes.filter(pc => pc.isActive);
  }

  get getSelectedPromoCode() {
    return this.selectedPromoCode;
  }

  get getIsLoading() {
    return this.isLoading;
  }

  get getCanCreate() {
    return this.canCreate;
  }

  get hasPromoCodes() {
    return this.promoCodes.length > 0;
  }
}

export default PromoCodeStore.getInstance();
